(function($, duo, undefined) {

	"use strict";

	// classes:
	const ACCORDION_BUTTON_CLASS = 'js-accordion-button';
	const ACCORDION_WRAPPER_CLASS = 'js-accordion-wrapper';

	// keycodes:
	const KEY_ARROWUP = 38;
	const KEY_ARROWDOWN = 40;
	const KEY_HOME = 36;
	const KEY_END = 35;

	/**
	 * @class Accordion
	 */
	class Accordion {

		/**
		 * Creates an instance of Accordion
		 * @param {string} containerSelector css selector for the Accordion container
		 * @param {string} itemSelector css selector for an accordion item
		 * @param {string} titleSelector css selector for the title of an accordion item
		 * @param {string} contentSelector css selector for the collapsible content
		 * @param {string} openClass css class that goes on the accordion item when it is expanded
		 * @param {number} openedIndex zero-based index of the accordion item that should be opened initially
		 * @param {boolean} enableKeyboardEvents set to true to enable keyboard events
		 * @param {boolean} onlyAllowOneOpenItem set to true to only allow one item to be opened
		 */
		constructor({containerSelector = '.accordion-container', itemSelector = '.accordion-item', titleSelector = '.accordion-title', contentSelector = 'accordion-content', openClass = 'js-item-opened', openedIndex, enableKeyboardEvents = false, onlyAllowOneOpenItem = false} = {}) {
			this.containerSelector = containerSelector;
			this.itemSelector = itemSelector;
			this.titleSelector = titleSelector;
			this.contentSelector = contentSelector;
			this.openClass = openClass;
			this.openedIndex = openedIndex;
			this.enableKeyboardEvents = enableKeyboardEvents;
			this.onlyAllowOneOpenItem = onlyAllowOneOpenItem;

			// setup accordions:
			this.setup();
		} /* constructor */

		/**
		 * Setup the selected accordions
		 */
		setup() {
			// setup markup:
			this._setupMarkup();
			// setup event handlers:
			this._setEventHandlers();

			// add js-processed class to the accordion container:
			$(this.containerSelector).addClass('js-accordion-processed');
		} /* setup */

		/**
		 * Setup the markup
		 */
		_setupMarkup() {
			const accordionClass = this;

			$(document).find(accordionClass.containerSelector).each(function(index, element) {
				const $accordion = $(element);

				// add data-attribute for accordion-index:
				$accordion.attr('data-accordion-index', index);

				// loop through each item:
				$accordion.find(accordionClass.itemSelector).each(function(i, accordionItemElement) {
					const $accordionItem = $(accordionItemElement);

					// -> get the title and the content elements:
					const $titleElement = $accordionItem.find(accordionClass.titleSelector);
					const $contentElement = $accordionItem.find(accordionClass.contentSelector);

					// -> create a unique ID for each collapsible-block:
					const id = 'accordion-' + duo.Utils.uniqueId;

					// -> wrap the content:
					$contentElement.wrap(`<div id="${id}" aria-hidden="true" class="${ACCORDION_WRAPPER_CLASS}" data-role="accordion-content">`);

					// -> create the toggle button:
					$titleElement.wrapInner(`<button aria-expanded="false" aria-controls="${id}" class="${ACCORDION_BUTTON_CLASS}" data-role="accordion-button">`);

					// -> if an "openedIndex" is defined, and it's the current item, we
					//    need to "open" it:
					if(accordionClass.openedIndex !== undefined) {
						if(i === accordionClass.openedIndex) {
							accordionClass._toggleItem($accordionItem);
						}
					}

				});
			});
		} /* _setupMarkup */

		/**
		 * Setup event handlers
		 */
		_setEventHandlers() {
			const accordionClass = this;

			/* toggle button click */
			/* ******************* */

			$(document).on('click', `.${ACCORDION_BUTTON_CLASS}`, function(event) {
				const $accordionButton = $(this);

				// get current accordion item:
				const $accordionItem = $accordionButton.parents(accordionClass.itemSelector);
				// toggle the current item:
				accordionClass._toggleItem($accordionItem);

				// if required, close all other items:
				if(accordionClass.onlyAllowOneOpenItem) {
					const $allAccordionItems = $accordionButton.parents(accordionClass.containerSelector).find(`${accordionClass.itemSelector}.${accordionClass.openClass}`);
					$allAccordionItems.each(function(index, element) {
						if(element !== $accordionItem[0]) {
							accordionClass._toggleItem($(element), 'close');
						}
					});
				}

			});

			/* keyboard events */
			/* *************** */

			if(accordionClass.enableKeyboardEvents) {

				/*
				 * extra navigation buttons are optional according to the
				 * aria best practices;
				 * if keyboard events are enabled, the following keys
				 * can be used: down arrow, up arrow, home, end
				 */

				$(`.${ACCORDION_BUTTON_CLASS}`).on('keydown.accordion', function(e) {
					const keyCode = e.keyCode || e.which;

					// get the current accordion container and get all of the toggle
					// buttons
					// -> then get the index of the current button

					const $currentButton = $(this);
					const $parentAccordion = $currentButton.parents(accordionClass.containerSelector);
					const $allAccordionButtons = $parentAccordion.find(`.${ACCORDION_BUTTON_CLASS}`);
					const buttonCount = $allAccordionButtons.length;

					// -> get the index of the current button:
					let currentIndex;
					$allAccordionButtons.each(function(index, element) {
						if(element === $currentButton[0]) {
							currentIndex = index;
						}
					});

					// -> check keycodes:
					if(!e.ctrlKey) {
						switch(keyCode) {
							case KEY_ARROWDOWN:
								// --> down arrow: move focus to the next accordion header
								e.preventDefault();
								let nextIndex = currentIndex + 1;
								if(nextIndex >= buttonCount) {
									nextIndex = 0;
								}
								$allAccordionButtons[nextIndex].focus();
								break;
							case KEY_ARROWUP:
								// --> up arrow: move focus to the previous accordion header
								e.preventDefault();
								let prevIndex = currentIndex - 1;
								if(prevIndex < 0) {
									prevIndex = buttonCount -1;
								}
								$allAccordionButtons[prevIndex].focus();
								break;
							case KEY_HOME:
								// --> home: move focus to the first accordion header
								e.preventDefault();
								$allAccordionButtons[0].focus();
								break;
							case KEY_END:
								// --> end: move focus to the last accordion header
								e.preventDefault();
								$allAccordionButtons[buttonCount - 1].focus();
								break;
							default: break;
						}
					}

				});

			}
		} /* _setEventHandlers */

		/**
		 * Toggle item
		 */
		_toggleItem($accordionItem, resultState) {
			const accordionClass = this;

			let $accordionButton;
			let $contentElement;

			if($accordionItem.length > 0) {
				$accordionButton = $accordionItem.find(`.${ACCORDION_BUTTON_CLASS}`);
				$contentElement = $accordionItem.find(`.${ACCORDION_WRAPPER_CLASS}`);
			}

			// change the markup:
			if($accordionItem.length > 0 && $accordionButton.length > 0 && $contentElement.length > 0) {

				// -> get the current expanded-state:
				let state = $accordionButton.attr('aria-expanded') === 'false' ? true : false;
				if(resultState !== undefined) {
					if(resultState === 'close') {
						state = false;
					} else if(resultState === 'open') {
						state = true;
					}
				}

				// -> set the expanded state on the button:
				$accordionButton.attr('aria-expanded', state);

				// -> set aria-hidden state on the content:
				$contentElement.attr('aria-hidden', !state);

				// -> set open-class on the container-item:
				if(state) {
					$accordionItem.addClass(accordionClass.openClass);
				} else {
					$accordionItem.removeClass(accordionClass.openClass);
				}

			}
		} /* _toggleItem */

	} /* Accordion */

	duo.Accordion = Accordion;

})(jQuery, window.duo = window.duo || {});
